import { orderThemes, searchThemes, themeDisplay, type ThemeOption } from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useAccess } from '../access/useAccess.ts'
import { useApi } from '../api-context.tsx'
import { notifyFailure } from '../feedback.ts'
import { servicesKeys } from './useServices.ts'

/**
 * A song's themes (docs/15-MUSIC-AND-AI.md, "Song themes from the shared
 * catalog"): what it wears — its recording's themes from the shared
 * catalog (or its own AI themes when it has none), less what the church
 * removed, with what it added — each marked with where it comes from, and
 * a Songs Editor's changes: remove one (×), add one from the vocabulary (a
 * combobox). The church's changes are its own; the catalog is never edited
 * from a church. Saved at once, shown at once (optimistic), put back on failure.
 */

export const songThemesKeys = {
  song: (songId: string) => [...servicesKeys.song(songId), 'themes'] as const,
}

/** How often to look again while a song's themes are on their way, and for how long. */
const WAITING_POLL_MS = 4000
const WAITING_POLLS = 15

export function useSongThemes(songId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  // Songs are church-wide (docs/13-ACCESS.md): Songs Editor changes them.
  const editable = useAccess().hasSomewhere('songs', 'editor')
  const key = songThemesKeys.song(songId)
  const query = useQuery({
    queryKey: key,
    enabled: songId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.songs({ id: songId }).themes.get()
      if (error) throw error
      return data
    },
    // The worker is giving them their themes: look again for a minute.
    refetchInterval: (q) =>
      q.state.data?.waiting && q.state.dataUpdateCount < WAITING_POLLS ? WAITING_POLL_MS : false,
  })
  type View = NonNullable<typeof query.data>
  const save = useMutation({
    mutationFn: async (themes: string[]) => {
      const { data, error } = await api.api.v1.services.songs({ id: songId }).themes.put({ themes })
      if (error) throw error
      return data
    },
    onMutate: async (themes) => {
      await queryClient.cancelQueries({ queryKey: key })
      const before = queryClient.getQueryData<View>(key)
      if (before) {
        const known = new Map(before.themes.map((t) => [t.value, t] as const))
        queryClient.setQueryData<View>(key, {
          ...before,
          themes: orderThemes(themes).map(
            (value) => known.get(value) ?? { value, label: themeDisplay(value), origin: 'church' },
          ),
        })
      }
      return { before }
    },
    onError: (error, _themes, context) => {
      if (context?.before) queryClient.setQueryData(key, context.before)
      notifyFailure(error, undefined, 'Couldn’t change its themes')
    },
    onSuccess: (data) => {
      queryClient.setQueryData(key, data)
      // The song's chips and the library's theme filter read them too.
      void queryClient.invalidateQueries({ queryKey: servicesKeys.song(songId), exact: true })
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'songs'] })
    },
  })

  const [search, setSearch] = useState('')
  const view = query.data
  const values = (view?.themes ?? []).map((t) => t.value)
  /** The vocabulary's themes for what is typed, without the song's. */
  const options: ThemeOption[] = searchThemes(search, values, 50)

  return {
    query,
    themes: view?.themes ?? [],
    /** It wears its recording's themes from the shared catalog. */
    fromCatalog: view?.fromCatalog ?? false,
    /** Its themes are on their way (the AI has not given them yet). */
    waiting: view?.waiting ?? false,
    editable,
    search,
    setSearch,
    options,
    saving: save.isPending,
    /** Adds a vocabulary theme (by slug). */
    add(slug: string) {
      if (values.includes(slug)) return
      setSearch('')
      save.mutate([...values, slug])
    },
    /** Takes a theme off the song (the church's choice; the catalog keeps it). */
    remove(value: string) {
      save.mutate(values.filter((v) => v !== value))
    },
  }
}

export type SongThemes = ReturnType<typeof useSongThemes>
